import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, CalendarClock, AlertCircle } from "lucide-react";

const EMPTY = { name: "", company: "", position: "", state: "" };

export function RequestDemoDialog({ open, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [states, setStates] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { if (open && !states.length) api.get("/states").then((r) => setStates(r.data)).catch(() => {}); }, [open, states.length]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.company.trim() && form.position.trim() && form.state.trim();

  const close = () => { if (sending) return; onClose(); setTimeout(() => { setForm(EMPTY); setDone(false); setError(false); }, 250); };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    setError(false);
    try {
      await api.post("/demo-request", form);
      setDone(true);
      setTimeout(() => close(), 2000);
    } catch {
      setError(true);
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="demo-dialog" className="max-w-md rounded-[28px] p-0 overflow-hidden border-0 card-shadow-lg">
        {done ? (
          <div className="flex flex-col items-center justify-center gap-3 px-8 py-14 text-center" data-testid="demo-thankyou">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-covered/10">
              <CheckCircle2 className="h-9 w-9 text-covered" />
            </div>
            <h3 className="font-display text-2xl text-navy">{t("demo_thanks_title")}</h3>
            <p className="text-slate-500">{t("demo_thanks_body")}</p>
          </div>
        ) : (
          <>
            <DialogHeader className="px-7 pt-7 pb-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl brand-gradient text-white mb-1"><CalendarClock className="h-6 w-6" /></div>
              <DialogTitle className="font-display text-2xl text-navy">{t("demo_title")}</DialogTitle>
              <DialogDescription className="text-slate-500 text-base">{t("demo_sub")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 px-7 pb-7 pt-2">
              <div>
                <Label className="text-navy">{t("demo_name")} *</Label>
                <Input data-testid="demo-name" required value={form.name} onChange={set("name")}
                  className="mt-1 rounded-xl" placeholder={t("demo_name_ph")} />
              </div>
              <div>
                <Label className="text-navy">{t("demo_company")} *</Label>
                <Input data-testid="demo-company" required value={form.company} onChange={set("company")}
                  className="mt-1 rounded-xl" placeholder={t("demo_company_ph")} />
              </div>
              <div>
                <Label className="text-navy">{t("demo_position")} *</Label>
                <Input data-testid="demo-position" required value={form.position} onChange={set("position")}
                  className="mt-1 rounded-xl" placeholder={t("demo_position_ph")} />
              </div>
              <div>
                <Label className="text-navy">{t("demo_state")} *</Label>
                <select data-testid="demo-state" required value={form.state} onChange={set("state")}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-navy focus:border-brandblue focus:outline-none">
                  <option value="" disabled>{t("demo_state_ph")}</option>
                  {states.map((s) => <option key={s.code} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <Button type="submit" data-testid="demo-submit" disabled={!valid || sending}
                className="w-full rounded-full brand-gradient text-white py-6 text-base font-semibold hover:opacity-90 disabled:opacity-50">
                {sending ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t("demo_sending")}</> : t("demo_send")}
              </Button>
              {error && (
                <p data-testid="demo-error" className="flex items-center justify-center gap-1.5 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" /> {t("demo_error")}
                </p>
              )}
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
